import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useAuth } from '../../contexts/AuthContext';
import { getCommissionRecords } from '../../lib/api';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
    Number(amount || 0),
  );

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const chipClass = (status) => (
  status === 'paid'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : status === 'approved'
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : 'bg-amber-50 text-amber-700 border-amber-200'
);

const normalizeRecord = (item) => ({
  id: item.id,
  loanId: item.loan_application_id,
  bank: item.bank_name || '—',
  rate: Number(item.applied_rate || 0),
  amount: Number(item.amount || 0),
  status: item.status,
  createdAt: item.created_at,
  paidAt: item.paid_at,
  calculationName: item.calculation_name,
});

export default function AgentEarnings() {
  const location = useLocation();
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const mode = (() => {
    if (location.pathname.includes('/agent/earnings/pending')) return 'pending';
    if (location.pathname.includes('/agent/earnings/paid')) return 'paid';
    if (location.pathname.includes('/agent/earnings/history')) return 'history';
    return 'month';
  })();

  useEffect(() => {
    let active = true;
    async function load() {
      if (!token) return;
      setLoading(true);
      setError('');
      try {
        const resp = await getCommissionRecords(token);
        const items = Array.isArray(resp?.data?.items) ? resp.data.items : [];
        if (active) {
          setRows(items.map(normalizeRecord));
        }
      } catch (err) {
        if (!active) return;
        setRows([]);
        setError(err?.message || 'Failed to load commission data');
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [token]);

  const filteredRows = useMemo(() => {
    if (mode === 'pending') return rows.filter((row) => row.status === 'pending');
    if (mode === 'paid') return rows.filter((row) => row.status === 'paid');
    if (mode === 'month') {
      const now = new Date();
      return rows.filter((row) => {
        const created = new Date(row.createdAt);
        return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
      });
    }
    return rows;
  }, [mode, rows]);

  const totals = useMemo(() => {
    const paid = rows.filter((row) => row.status === 'paid').reduce((sum, row) => sum + row.amount, 0);
    const pending = rows.filter((row) => row.status === 'pending').reduce((sum, row) => sum + row.amount, 0);
    const approved = rows.filter((row) => row.status === 'approved').reduce((sum, row) => sum + row.amount, 0);
    return { paid, pending, approved, total: paid + pending + approved };
  }, [rows]);

  const title =
    mode === 'pending'
      ? 'Pending Commission'
      : mode === 'paid'
        ? 'Paid Commission'
        : mode === 'history'
          ? 'Commission History'
          : 'This Month';

  return (
    <div className="space-y-6" data-testid="agent-earnings">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">{title}</h1>
          <p className="text-muted-foreground mt-1">Track your commission entries from disbursed files.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button asChild size="sm" variant={mode === 'month' ? 'default' : 'outline'}>
            <Link to="/agent/earnings">This Month</Link>
          </Button>
          <Button asChild size="sm" variant={mode === 'pending' ? 'default' : 'outline'}>
            <Link to="/agent/earnings/pending">Pending</Link>
          </Button>
          <Button asChild size="sm" variant={mode === 'paid' ? 'default' : 'outline'}>
            <Link to="/agent/earnings/paid">Paid</Link>
          </Button>
          <Button asChild size="sm" variant={mode === 'history' ? 'default' : 'outline'}>
            <Link to="/agent/earnings/history">History</Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border border-border">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Total Earned</p>
            <p className="text-2xl font-bold">{formatCurrency(totals.total)}</p>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Paid</p>
            <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totals.paid)}</p>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Pending</p>
            <p className="text-2xl font-bold text-amber-600">{formatCurrency(totals.pending)}</p>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Approved</p>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(totals.approved)}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Commission Ledger</CardTitle>
          <CardDescription>Real commission entries mapped to your account.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Loan</TableHead>
                <TableHead>Bank</TableHead>
                <TableHead>Calc</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    Loading commission entries...
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && error ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    {error}
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && !error && filteredRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-sm">{row.loanId}</TableCell>
                  <TableCell>{row.bank}</TableCell>
                  <TableCell className="capitalize">{row.calculationName}</TableCell>
                  <TableCell>{row.calculationName === 'flat' ? formatCurrency(row.rate) : `${row.rate}%`}</TableCell>
                  <TableCell className="font-medium">{formatCurrency(row.amount)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={chipClass(row.status)}>
                      {row.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(row.paidAt || row.createdAt)}</TableCell>
                </TableRow>
              ))}
              {!loading && !error && filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    No commission entries found.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
